import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, InputNumber, Modal, Row, Select, Table, message } from 'antd';
import MonthPicker from 'components/MonthPicker/MonthPicker';
import moment from 'moment';
import { autoCalculateRevenue, listRevenue, listServicePackages, updateBookingCleaningFee, upsertRevenue } from '../api/ownerReporting';
import { loadHostProperties, loadHostUnits, loadUnitsForProperty } from '../api/hostListings';
import { useOwnerReportingHost } from '../hooks/useOwnerReportingHost';
import { MONTH_FORMAT } from 'utils/constants';
import {
  availableDaysForContract,
  payoutMethodLabel,
  payoutWindow,
  resolveAutoAvailableDays,
} from './servicePackages/formShared';
import { readPageFilters, rowsForSelectedMonth, toMonthDate, useSyncPageFilters } from './pageFilters';
import { downloadCsvFile } from './revenueCsv';
import RevenueBookingsModal from '../components/RevenueBookingsModal';
import tableStyles from './tableOverflow.module.css';

const OTA_CHANNELS = [
  { label: 'Agoda', field: 'totalAgoda' },
  { label: 'Airbnb', field: 'totalAirbnb' },
  { label: 'Booking.com', field: 'totalBookingDotCom' },
  { label: 'Ctrip', field: 'totalCtrip' },
  { label: 'Expedia', field: 'totalExpedia' },
  { label: 'Traveloka', field: 'totalTraveloka' },
  { label: 'Tiket.com', field: 'totalTicketDotCom' },
];

const emptyChannelValues = () =>
  OTA_CHANNELS.reduce((acc, ch) => {
    acc[ch.field] = 0;
    return acc;
  }, {});

const formatMoney = n => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumOtaChannels = values =>
  OTA_CHANNELS.reduce((sum, ch) => sum + (Number(values && values[ch.field]) || 0), 0);

const grandTotal = values =>
  (Number(values && values.totalOTAs) || 0) + (Number(values && values.totalOfflineSales) || 0);

const nightsBetween = (checkIn, checkOut) => {
  const start = checkIn ? new Date(checkIn) : null;
  const end = checkOut ? new Date(checkOut) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
};

const getBookingStats = record => {
  if (record && (record.totalBookings != null || record.totalNights != null)) {
    return {
      bookings: Number(record.totalBookings) || 0,
      nights: Number(record.totalNights) || 0,
    };
  }
  const contributions = (record && record.reservationContributions) || [];
  const active = contributions.filter(c => c && c.status !== 'cancelled');
  return {
    bookings: active.length,
    nights: active.reduce(
      (sum, c) => sum + nightsBetween(c.checkInDate, c.checkOutDate),
      0
    ),
  };
};

const normalizeId = id => (id && id._id ? String(id._id) : String(id));

const normalizePackageId = pkg => {
  if (!pkg) return undefined;
  if (typeof pkg === 'object') return String(pkg._id || pkg);
  return String(pkg);
};

const packageForRecord = (record, packages) => {
  const pkgId = normalizePackageId(record && record.servicePackage);
  return (packages || []).find(p => String(p._id) === String(pkgId));
};

const recordPayoutWindow = (record, packages) => {
  const pkg = packageForRecord(record, packages);
  return payoutWindow(record.year, record.month, pkg && pkg.startDay);
};

const csvMoney = n => {
  const num = Number(n);
  return Number.isFinite(num) && num ? num.toFixed(2) : 0;
};

const csvDate = value => {
  if (!value) return '';
  const str = String(value);
  return str.length >= 10 ? str.slice(0, 10) : str;
};

const channelLabel = platform => {
  const key = String(platform || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const labels = {
    airbnb: 'Airbnb',
    agoda: 'Agoda',
    agodahomes: 'Agoda Homes',
    bookingcom: 'Booking.com',
    bookingdotcom: 'Booking.com',
    ctrip: 'Ctrip (Deprecated)',
    ctripcm: 'Ctrip',
    expedia: 'Expedia',
    traveloka: 'Traveloka',
    tiketcom: 'Tiket.com',
    ticketdotcom: 'Tiket.com',
    hostplatform: 'HostPlatform',
    bookingengine: 'Homes Booking Website',
    hotelbookingengine: 'Hotel Booking Website',
    homeaway: 'HomeAway',
    others: 'Others',
    maplehome: 'Maple Home',
    maple: 'Maple Home',
    offline: 'Walk In',
    walkin: 'Walk In',
  };
  return labels[key] || platform || '';
};

const buildFormValues = (base, overrides = {}) => {
  const next = {
    servicePackage: undefined,
    year: base.year,
    month: base.month,
    ...emptyChannelValues(),
    totalOTAs: 0,
    totalOfflineSales: 0,
    totalOffline: 0,
    ...overrides,
  };
  next.totalOTAs = sumOtaChannels(next);
  return next;
};

const RevenuePage = ({ location, history }) => {
  const { hostId } = useOwnerReportingHost();
  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [properties, setProperties] = useState([]);
  const [hostUnits, setHostUnits] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [propertyId, setPropertyId] = useState(() => readPageFilters(location && location.search).propertyId);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [adjustVisible, setAdjustVisible] = useState(false);
  const [daysDraftByRecordId, setDaysDraftByRecordId] = useState({});
  const [savingDaysId, setSavingDaysId] = useState(null);
  const [bookingsModalRecord, setBookingsModalRecord] = useState(null);
  const [savingFeeId, setSavingFeeId] = useState(null);
  const [monthDate, setMonthDate] = useState(() => readPageFilters(location && location.search).monthDate);
  const [values, setValues] = useState(() => {
    const initial = readPageFilters(location && location.search);
    return buildFormValues({
      year: initial.monthDate.year(),
      month: initial.monthDate.month() + 1,
    });
  });

  const packageNameById = useMemo(() => {
    const map = {};
    (packages || []).forEach(p => {
      map[String(p._id)] = p.name;
    });
    return map;
  }, [packages]);

  const unitMetaById = useMemo(() => {
    const map = {};
    const addUnit = u => {
      if (!u || u._id == null) return;
      const roomType = u.roomType || {};
      const property = roomType.property && typeof roomType.property === 'object' ? roomType.property : {};
      const id = String(u._id);
      const prev = map[id] || {};
      map[id] = {
        unitName: u.name || prev.unitName || id,
        roomTypeName: roomType.name || prev.roomTypeName || '',
        propertyName: property.name || prev.propertyName || '',
        contractPeriod: u.contractPeriod || prev.contractPeriod,
      };
    };
    (hostUnits || []).forEach(addUnit);
    (propertyUnits || []).forEach(addUnit);
    return map;
  }, [hostUnits, propertyUnits]);

  const propertyUnitIds = useMemo(() => {
    if (!propertyId) return null;
    return new Set(
      (hostUnits || [])
        .filter(u => {
          const property = u.roomType && u.roomType.property;
          const pid = property && property._id ? property._id : property;
          return String(pid) === String(propertyId);
        })
        .map(u => String(u._id))
    );
  }, [hostUnits, propertyId]);

  const packagesForProperty = useMemo(() => {
    if (!propertyId) return packages || [];
    const unitIds = propertyUnitIds || new Set();
    return (packages || []).filter(pkg =>
      (pkg.units || []).some(uid => unitIds.has(normalizeId(uid)))
    );
  }, [packages, propertyId, propertyUnitIds]);

  const packageIdsForProperty = useMemo(
    () => new Set((packagesForProperty || []).map(p => String(p._id))),
    [packagesForProperty]
  );

  const filteredRows = useMemo(() => {
    if (!propertyId) return rows || [];
    return (rows || []).filter(r => packageIdsForProperty.has(normalizePackageId(r.servicePackage)));
  }, [rows, propertyId, packageIdsForProperty]);

  const monthRows = useMemo(
    () => rowsForSelectedMonth(filteredRows, values.year, values.month),
    [filteredRows, values.year, values.month]
  );

  useEffect(() => {
    setBookingsModalRecord(prev => {
      if (!prev) return prev;
      const next = monthRows.find(r => String(r._id) === String(prev._id));
      return next || prev;
    });
  }, [monthRows]);

  const monthSummary = useMemo(() => {
    return monthRows.reduce(
      (acc, r) => {
        acc.totalOTAs += Number(r.totalOTAs) || 0;
        acc.totalOfflineSales += Number(r.totalOfflineSales != null ? r.totalOfflineSales : r.totalOffline) || 0;
        return acc;
      },
      { totalOTAs: 0, totalOfflineSales: 0 }
    );
  }, [monthRows]);

  const salesExportRows = useMemo(() => {
    const lines = [];
    monthRows.forEach(record => {
      ((record && record.reservationContributions) || []).forEach(c => {
        if (!c || c.status === 'cancelled') return;
        lines.push(c);
      });
    });
    return lines;
  }, [monthRows]);

  const salesCsvData = useMemo(
    () =>
      salesExportRows.map(c => {
        const unitId = normalizeId(c.unitId);
        const meta = unitMetaById[unitId] || {};
        const rental = c.amount;
        const cleaning = c.cleaningFee;
        return {
          'Booking Source': channelLabel(c.platform),
          'Booking Type': c.bookingType || '',
          'Property Name': meta.propertyName || '',
          'Room Type': meta.roomTypeName || '',
          'Unit Name': meta.unitName || unitId || '',
          'Confirmation Code': c.code || normalizeId(c.reservationId),
          'OTA Confirmation Code': c.sourceId || '',
          'Check-In': csvDate(c.checkInDate),
          'Check-Out': csvDate(c.checkOutDate),
          'Booked Nights': nightsBetween(c.checkInDate, c.checkOutDate),
          'Guest Name': c.guestName || '',
          Nationality: c.nationality || '',
          'Number of pax': Number(c.numberOfPax) || 0,
          'Rental Revenue': csvMoney(rental),
          'Cleaning Revenue (Auto)': csvMoney(cleaning),
          'Add On Revenue': csvMoney(c.addOnAmount),
          'Platform Fee': csvMoney(c.platformFee),
          'Other Revenue': csvMoney(c.otherAmount),
          'Sales Service Tax': csvMoney(c.taxSst),
          'Tourism Tax': csvMoney(c.taxTourism),
          'Heritage Tax': csvMoney(c.taxHeritage),
          'Sales Tax': csvMoney(c.taxSales),
          'OTA Tax': csvMoney(c.taxOta),
          Currency: c.currency || '',
        };
      }),
    [salesExportRows, unitMetaById]
  );

  const selectedPropertyName = useMemo(() => {
    const found = (properties || []).find(p => String(p._id) === String(propertyId));
    return (found && found.name) || '';
  }, [properties, propertyId]);

  const salesCsvFilename = useMemo(() => {
    const monthStr = Number(values.month) < 10 ? `0${Number(values.month)}` : String(values.month);
    const prefix = `${values.year}_${monthStr}_${selectedPropertyName}__reporting overview`;
    return `${prefix}_MONTHLY REVENUE BREAKDOWN.csv`;
  }, [values.year, values.month, selectedPropertyName]);

  const loadFilters = async () => {
    if (!hostId) return;
    setPropertiesLoading(true);
    try {
      const [pkgs, props, units] = await Promise.all([
        listServicePackages(hostId),
        loadHostProperties(hostId),
        loadHostUnits(hostId),
      ]);
      setPackages(pkgs || []);
      const propsList = props || [];
      setProperties(propsList);
      setHostUnits(units || []);
      setPropertyId(prev => {
        if (prev && propsList.some(p => String(p._id) === String(prev))) return prev;
        return propsList[0] ? String(propsList[0]._id) : undefined;
      });
    } catch (e) {
      message.error('Failed to load revenue filters');
    } finally {
      setPropertiesLoading(false);
    }
  };

  const reloadRows = async year => {
    if (!hostId) return;
    const rev = await listRevenue(hostId, { year: year || values.year });
    setRows(rev || []);
  };

  useEffect(() => {
    if (!hostId) {
      setPackages([]);
      setProperties([]);
      setHostUnits([]);
      setPropertyUnits([]);
      setPropertyId(undefined);
      setRows([]);
      return undefined;
    }
    setRows([]);
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  useEffect(() => {
    if (!propertyId) {
      setPropertyUnits([]);
      return undefined;
    }
    let cancelled = false;
    loadUnitsForProperty(propertyId).then(list => {
      if (!cancelled) setPropertyUnits(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    if (!hostId || !propertyId || propertiesLoading) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.resolve()
      .then(async () => {
        if (packagesForProperty.length) {
          await Promise.allSettled(
            packagesForProperty.map(pkg =>
              autoCalculateRevenue(hostId, {
                servicePackage: pkg._id,
                year: values.year,
                month: values.month,
              })
            )
          );
        }
        const rev = await listRevenue(hostId, { year: values.year });
        if (!cancelled) setRows(rev || []);
      })
      .catch(() => {
        if (!cancelled) message.error('Failed to load revenue');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId, propertyId, values.year, values.month, packages, hostUnits, propertiesLoading, refreshTick]);

  useSyncPageFilters(history, location, {
    property: propertyId,
    date: monthDate.format(MONTH_FORMAT),
  });

  const set = key => val => setValues(prev => ({ ...prev, [key]: val }));

  const setChannel = field => val => {
    setValues(prev => {
      const next = { ...prev, [field]: val == null ? 0 : Number(val) };
      next.totalOTAs = sumOtaChannels(next);
      return next;
    });
  };

  const setWalkIn = val => {
    const amount = val == null ? 0 : Number(val);
    setValues(prev => ({
      ...prev,
      totalOfflineSales: amount,
      totalOffline: amount,
    }));
  };

  const onMonthChange = date => {
    const m = toMonthDate(date);
    const year = m.year();
    const month = m.month() + 1;
    setMonthDate(m);
    setValues(prev => ({
      ...prev,
      year,
      month,
    }));
  };

  const openAdjustFromRow = row => {
    const channelOverrides = OTA_CHANNELS.reduce((acc, ch) => {
      acc[ch.field] = Number(row[ch.field]) || 0;
      return acc;
    }, {});
    const walkIn = Number(row.totalOfflineSales != null ? row.totalOfflineSales : row.totalOffline) || 0;
    setValues(
      buildFormValues(
        { year: row.year, month: row.month },
        {
          servicePackage: normalizePackageId(row.servicePackage),
          ...channelOverrides,
          totalOfflineSales: walkIn,
          totalOffline: walkIn,
        }
      )
    );
    setMonthDate(moment({ year: row.year, month: row.month - 1 }));
    setAdjustVisible(true);
  };

  const onAdjust = async () => {
    if (!values.servicePackage) {
      message.error('Service package is required');
      return;
    }
    const totalOTAs = sumOtaChannels(values);
    const walkIn = Number(values.totalOfflineSales) || 0;
    const payload = {
      servicePackage: values.servicePackage,
      year: values.year,
      month: values.month,
      ...OTA_CHANNELS.reduce((acc, ch) => {
        acc[ch.field] = Number(values[ch.field]) || 0;
        return acc;
      }, {}),
      totalOTAs,
      totalOfflineSales: walkIn,
      totalOffline: walkIn,
    };
    try {
      await upsertRevenue(hostId, payload);
      message.success('Saved');
      setAdjustVisible(false);
      await reloadRows(values.year);
    } catch (e) {
      message.error('Save failed');
    }
  };

  const liveTotalOTAs = sumOtaChannels(values);
  const liveGrandTotal = liveTotalOTAs + (Number(values.totalOfflineSales) || 0);

  const getAvailableDaysRows = record => {
    const pkgId = normalizePackageId(record.servicePackage);
    const pkg = (packages || []).find(p => String(p._id) === String(pkgId));
    const pkgUnitIds = (pkg && pkg.units && pkg.units.length
      ? pkg.units
      : (record.units || []).map(u => u.unitId)
    ).map(normalizeId);
    const savedById = {};
    (record.units || []).forEach(u => {
      if (!u || u.unitId == null) return;
      savedById[normalizeId(u.unitId)] = u.availableDays;
    });
    const draft = daysDraftByRecordId[record._id] || {};
    const window = recordPayoutWindow(record, packages);
    return pkgUnitIds.map(id => {
      const meta = unitMetaById[id] || {};
      const fromDraft = Object.prototype.hasOwnProperty.call(draft, id) ? draft[id] : undefined;
      const fromSaved = savedById[id];
      const computed = availableDaysForContract(window, meta.contractPeriod);
      const days =
        fromDraft != null
          ? fromDraft
          : resolveAutoAvailableDays({
              existing: fromSaved,
              daysInWindow: window.daysInWindow,
              computed,
            });
      return {
        unitId: id,
        name: meta.unitName || id,
        availableDays: Number(days),
      };
    });
  };

  const setUnitAvailableDays = (recordId, unitId, val) => {
    const next = val == null ? 0 : Number(val);
    setDaysDraftByRecordId(prev => ({
      ...prev,
      [recordId]: {
        ...(prev[recordId] || {}),
        [unitId]: Number.isFinite(next) ? next : 0,
      },
    }));
  };

  const onSaveAvailableDays = async record => {
    const rows = getAvailableDaysRows(record);
    if (!rows.length) {
      message.error('No units to save');
      return;
    }
    setSavingDaysId(record._id);
    try {
      await upsertRevenue(hostId, {
        servicePackage: normalizePackageId(record.servicePackage),
        year: record.year,
        month: record.month,
        units: rows.map(r => ({ unitId: r.unitId, availableDays: Number(r.availableDays) || 0 })),
      });
      message.success('Available days saved');
      setDaysDraftByRecordId(prev => {
        const next = { ...prev };
        delete next[record._id];
        return next;
      });
      await reloadRows(record.year);
    } catch (e) {
      message.error('Failed to save available days');
    } finally {
      setSavingDaysId(null);
    }
  };

  const onSaveCleaningFee = async payload => {
    if (!bookingsModalRecord || !hostId || !payload || !payload.reservationId) return;
    setSavingFeeId(String(payload.reservationId));
    try {
      const result = await updateBookingCleaningFee(hostId, {
        reservationId: payload.reservationId,
        amount: payload.amount,
        servicePackage: normalizePackageId(bookingsModalRecord.servicePackage),
        year: bookingsModalRecord.year,
        month: bookingsModalRecord.month,
      });
      if (result && result.skippedAuditedCount) {
        message.warning('Cleaning fee saved. Audited statements were not updated.');
      } else {
        message.success('Cleaning fee saved');
      }
      await reloadRows(bookingsModalRecord.year);
    } catch (e) {
      message.error('Failed to save cleaning fee');
    } finally {
      setSavingFeeId(null);
    }
  };

  const expandedRowRender = record => {
    const walkIn = Number(record.totalOfflineSales != null ? record.totalOfflineSales : record.totalOffline) || 0;
    const pkg = packageForRecord(record, packages);
    const window = recordPayoutWindow(record, packages);
    const detailRows = [
      ...OTA_CHANNELS.map(ch => ({
        key: ch.field,
        channel: ch.label,
        amount: Number(record[ch.field]) || 0,
      })),
      { key: 'walkIn', channel: 'Walk-in', amount: walkIn },
    ];
    const contributions = (record.reservationContributions || []).filter(c => c && c.status !== 'cancelled');
    const daysRows = getAvailableDaysRows(record);
    const poolDays = daysRows.reduce((sum, r) => sum + (Number(r.availableDays) || 0), 0);
    const maxDays = window.daysInWindow;
    return (
      <div>
        <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 12 }}>
          Payout window {window.startDate} – {window.endDate} ({payoutMethodLabel(pkg && pkg.payoutCalcMethod)})
        </div>
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          dataSource={detailRows}
          columns={[
            { title: 'Channel', dataIndex: 'channel' },
            {
              title: 'Amount',
              dataIndex: 'amount',
              align: 'right',
              render: v => formatMoney(v),
            },
          ]}
        />
        {daysRows.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>
              Available days of service in this payout window. Pool share is unit days / total pool days (e.g. 25 / 55).
            </div>
            <Table
              size="small"
              pagination={false}
              rowKey="unitId"
              dataSource={daysRows}
              columns={[
                { title: 'Unit', dataIndex: 'name' },
                {
                  title: 'Available days',
                  width: 160,
                  render: r => (
                    <InputNumber
                      min={0}
                      max={maxDays}
                      value={r.availableDays}
                      onChange={val => setUnitAvailableDays(record._id, r.unitId, val)}
                    />
                  ),
                },
                {
                  title: 'Share',
                  width: 120,
                  align: 'right',
                  render: r => {
                    const days = Number(r.availableDays) || 0;
                    if (!poolDays) return `0 / ${poolDays}`;
                    return `${days} / ${poolDays}`;
                  },
                },
              ]}
              footer={() => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    Pool days: {poolDays} (window {maxDays})
                  </span>
                  <Button
                    type="primary"
                    size="small"
                    loading={savingDaysId === record._id}
                    onClick={() => onSaveAvailableDays(record)}
                  >
                    Save days
                  </Button>
                </div>
              )}
            />
          </div>
        )}
        {contributions.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Button onClick={() => setBookingsModalRecord(record)}>
              {`View bookings (${contributions.length})`}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card title="Owner Reporting — Monthly Revenue">
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} type="flex" align="bottom">
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item label="Property" required style={{ marginBottom: 0 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
                placeholder={propertiesLoading ? 'Loading properties...' : 'Select property'}
                value={propertyId}
                onChange={val => setPropertyId(val ? String(val) : undefined)}
                disabled={!hostId}
                loading={propertiesLoading}
              >
                {properties.map(p => (
                  <Select.Option key={String(p._id)} value={String(p._id)}>
                    {p.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={10} lg={9}>
            <Form.Item label="Month" style={{ marginBottom: 0 }}>
              <MonthPicker defaultValue={monthDate} onChange={onMonthChange} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={6} lg={8}>
            <Form.Item label=" " colon={false} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <Button
                  type="primary"
                  onClick={() => setRefreshTick(tick => tick + 1)}
                  loading={loading}
                  disabled={!hostId || !propertyId || loading}
                >
                  Refresh
                </Button>
                <Button
                  icon="download"
                  disabled={loading || salesCsvData.length === 0}
                  onClick={() => downloadCsvFile(salesCsvFilename, salesCsvData)}
                >
                  Download CSV
                </Button>
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Auto-calculate uses each package's payout method and start day"
        description="Start day 1 is the calendar month. Start day 15 in August is 15 August to 14 September. Listing contract start and end clip each unit's available days (15 August start in August is 15–31, 17 days). Per check-out uses checkout in that window; per transaction uses charge date; per day splits rental by night. Expand a row to edit available days, then Save days. View bookings to page through reservations and edit a cleaning fee."
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Total OTAs</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(monthSummary.totalOTAs)}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Walk-in</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(monthSummary.totalOfflineSales)}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <div style={{ color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}>Grand Total</div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{formatMoney(grandTotal(monthSummary))}</div>
          </Card>
        </Col>
      </Row>

      <div className={tableStyles.tableWrap}>
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={monthRows}
          expandedRowRender={expandedRowRender}
          scroll={{ x: 1200 }}
          columns={[
            {
              title: 'Service Package',
              width: 320,
              render: r => {
                const name =
                  packageNameById[normalizePackageId(r.servicePackage)] ||
                  normalizePackageId(r.servicePackage) ||
                  '—';
                return <span title={name}>{name}</span>;
              },
            },
            { title: 'Year', dataIndex: 'year', width: 80 },
            { title: 'Month', dataIndex: 'month', width: 80 },
            {
              title: 'Total OTAs',
              dataIndex: 'totalOTAs',
              width: 130,
              align: 'right',
              render: v => formatMoney(v),
            },
            {
              title: 'Walk-in',
              dataIndex: 'totalOfflineSales',
              width: 120,
              align: 'right',
              render: (v, r) => formatMoney(v != null ? v : r.totalOffline),
            },
            {
              title: 'Grand Total',
              width: 130,
              align: 'right',
              render: r => formatMoney(grandTotal(r)),
            },
            {
              title: 'Bookings',
              width: 100,
              align: 'right',
              render: r => getBookingStats(r).bookings,
            },
            {
              title: 'Nights',
              width: 90,
              align: 'right',
              render: r => getBookingStats(r).nights,
            },
            {
              title: 'Actions',
              width: 90,
              render: r => (
                <Button type="link" onClick={() => openAdjustFromRow(r)} style={{ padding: 0 }}>
                  Adjust
                </Button>
              ),
            },
          ]}
        />
      </div>

      <Modal
        visible={adjustVisible}
        title="Adjust Revenue (manual override)"
        onOk={onAdjust}
        onCancel={() => setAdjustVisible(false)}
        destroyOnClose
        width={720}
      >
        <Form layout="vertical">
          <Form.Item label="Service Package" required>
            <Select value={values.servicePackage} onChange={set('servicePackage')} style={{ width: '100%' }}>
              {packages.map(p => (
                <Select.Option key={p._id} value={p._id}>
                  {p.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Year" required>
                <InputNumber style={{ width: '100%' }} value={values.year} onChange={set('year')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Month" required>
                <InputNumber min={1} max={12} style={{ width: '100%' }} value={values.month} onChange={set('month')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            {OTA_CHANNELS.map(ch => (
              <Col xs={24} sm={12} key={ch.field}>
                <Form.Item label={ch.label}>
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    value={values[ch.field]}
                    onChange={setChannel(ch.field)}
                  />
                </Form.Item>
              </Col>
            ))}
            <Col xs={24} sm={12}>
              <Form.Item label="Walk-in">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  value={values.totalOfflineSales}
                  onChange={setWalkIn}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Total OTAs">
                <InputNumber style={{ width: '100%' }} value={liveTotalOTAs} disabled precision={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Grand Total">
                <InputNumber style={{ width: '100%' }} value={liveGrandTotal} disabled precision={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <RevenueBookingsModal
        visible={!!bookingsModalRecord}
        packageName={
          bookingsModalRecord
            ? packageNameById[normalizePackageId(bookingsModalRecord.servicePackage)] || 'Bookings'
            : 'Bookings'
        }
        contributions={
          bookingsModalRecord && bookingsModalRecord.reservationContributions
            ? bookingsModalRecord.reservationContributions
            : []
        }
        unitMetaById={unitMetaById}
        savingReservationId={savingFeeId}
        onCancel={() => setBookingsModalRecord(null)}
        onSaveFee={onSaveCleaningFee}
      />
    </Card>
  );
};

export default withRouter(RevenuePage);
